/**
 * Qué hacer ahora, cuando la app todavía está vacía.
 *
 * Un estado vacío que sólo dice "no hay datos" deja al usuario en la misma situación que antes de
 * abrir la app. Estos pasos se calculan de lo que efectivamente falta, cada uno lleva al lugar
 * donde se hace, y la tarjeta desaparece sola cuando ya no hay nada pendiente. No es un tutorial:
 * es la lista de cosas que la app necesita para servir de algo.
 *
 * Regla que no se puede romper: **un paso no puede contar distinto que la pantalla que lo cumple.**
 * Si acá dice "vas 2 de 3" y Cuerpo dice "hay 1", el usuario se pesa de nuevo creyendo que destraba
 * el número y no lo destraba — y deja de creerle a los dos carteles.
 */

import { useMemo, type ReactNode } from 'react';
import { MIN_LECTURAS_COMPARABLES, pRatio } from '@mg/core';

import type { Datos } from '../features/useDatos.ts';
import { Icono, Pila, type NombreIcono } from './componentes.tsx';
import { useRuta, type Pantalla } from './router.tsx';

/**
 * La misma ventana con la que Inicio y Cuerpo piden la calidad de la pérdida
 * (`features/useInicio.ts`, `features/useCuerpo.ts`). Va acá para que el paso cuente sobre
 * exactamente el mismo conjunto de lecturas que el KPI que promete destrabar.
 */
const VENTANA_PRATIO_DIAS = 28;

export interface Paso {
  clave: string;
  titulo: string;
  porque: string;
  icono: NombreIcono;
  destino: Pantalla;
  accion: string;
  hecho: boolean;
}

export function pasosPendientes(datos: Datos): Paso[] {
  const conSeries = datos.series.some((s) => s.completada);
  const conComidas = datos.comidas.length > 0;
  const conAltura = datos.perfil?.altura_cm !== null && datos.perfil?.altura_cm !== undefined;

  // El paso de la balanza se mide con la MISMA función que calcula lo que promete destrabar:
  // `pRatio` de @mg/core, con la ventana y el `hasta` que usan Inicio y Cuerpo. Antes acá se
  // contaba `datos.medidas.length` —todas las lecturas de la historia, sin ventana— y la app se
  // contradecía sola: "Vas 2 de 3" acá mientras Cuerpo decía "Hacen falta 3 lecturas en los
  // últimos 28 días; hay 1". Contar con la función en vez de replicar su filtro es lo que hace
  // imposible que las dos cuentas vuelvan a separarse.
  //
  // `pratio.lecturas` es literalmente el número que Cuerpo imprime: las lecturas dentro de la
  // ventana. Y el paso se da por hecho cuando el KPI ya se puede responder, no cuando se juntaron
  // tres papeles: con tres lecturas de puro peso, sin % de grasa ni masa magra, la pregunta "¿lo
  // que bajé fue grasa?" sigue sin respuesta, y el paso lo dice en vez de tildarse.
  const pratio = pRatio(datos.medidas, datos.perfil?.altura_cm ?? null, {
    ventanaDias: VENTANA_PRATIO_DIAS,
    hasta: datos.hoy,
  });
  const lecturas = pratio.lecturas;
  const faltanLecturas = pratio.estado === 'insuficiente';
  const faltaGrasa = pratio.estado === 'sin_datos_grasa';
  const faltan = MIN_LECTURAS_COMPARABLES - lecturas;

  return [
    {
      clave: 'serie',
      titulo: 'Registrá tu primera serie',
      porque: 'Con una sola serie la app ya empieza a sugerirte cuándo subir la placa.',
      icono: 'mancuerna',
      destino: 'registro',
      accion: 'Entrenar',
      hecho: conSeries,
    },
    {
      clave: 'medida',
      titulo: faltaGrasa ? 'Anotá el % de grasa' : `Pesate ${MIN_LECTURAS_COMPARABLES} veces`,
      porque: faltaGrasa
        ? `Tenés ${lecturas} ${lecturas === 1 ? 'lectura' : 'lecturas'}, pero ninguna trae el % de grasa ni la masa magra: sin eso no se puede saber qué parte de lo que bajaste era grasa. Copialo de la balanza en la próxima.`
        : lecturas === 0
          ? `Hacen falta ${MIN_LECTURAS_COMPARABLES} lecturas en los últimos ${VENTANA_PRATIO_DIAS} días para saber si lo que bajás es grasa. La primera te toma un minuto.`
          : faltan > 0
            ? `Vas ${lecturas} de ${MIN_LECTURAS_COMPARABLES} lecturas en los últimos ${VENTANA_PRATIO_DIAS} días. Con ${faltan === 1 ? 'una más' : `${faltan} más`}, la app ya puede decirte si lo que bajás es grasa.`
            : pratio.detalle,
      icono: 'balanza',
      destino: 'cuerpo',
      accion: 'Medirme',
      hecho: !faltanLecturas && !faltaGrasa,
    },
    {
      clave: 'comida',
      titulo: 'Anotá lo que comiste hoy',
      porque: 'Escribilo en una línea y la IA estima las calorías y la proteína.',
      icono: 'comida',
      destino: 'nutricion',
      accion: 'Anotar',
      hecho: conComidas,
    },
    {
      clave: 'altura',
      titulo: 'Completá tu altura',
      porque: 'Es lo único que falta para calcular tu FFMI.',
      icono: 'ajustes',
      destino: 'ajustes',
      accion: 'Completar',
      hecho: conAltura,
    },
  ];
}

export function PrimerosPasos({ datos, compacto = false }: { datos: Datos; compacto?: boolean }): ReactNode {
  const { ir } = useRuta();
  const pasos = useMemo(() => pasosPendientes(datos), [datos]);
  const hechos = pasos.filter((p) => p.hecho).length;

  if (hechos === pasos.length) return null;

  return (
    <section className="tarjeta" style={{ borderColor: 'var(--ambar)' }}>
      <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
        <span className="eyebrow">Para empezar</span>
        <span className="nota--fina lectura">
          {hechos} / {pasos.length}
        </span>
      </div>

      <Pila
        estados={pasos.map((p) => (p.hecho ? 'llena' : 'hueca'))}
        alto={8}
        etiqueta={`${hechos} de ${pasos.length} pasos hechos`}
      />

      {/* Se listan TODOS los pasos, en el mismo orden que las placas de arriba: cada placa llena
          tiene abajo la línea que la llenó. Antes sólo se listaban los pendientes, así que la
          pila decía "1 de 4" y debajo había tres renglones — el paso cumplido desaparecía y no
          había forma de verificar cuál había sido. Los hechos van atenuados, con tilde y sin
          botón: se leen como registro de lo hecho, no como algo que todavía reclama un toque. */}
      <div className="columna" style={{ gap: compacto ? 'var(--e2)' : 'var(--e3)', marginTop: 'var(--e4)' }}>
        {pasos.map((p) => (
          <div
            key={p.clave}
            className="fila fila--entre"
            style={{ gap: 'var(--e3)', alignItems: 'flex-start', opacity: p.hecho ? 0.6 : 1 }}
          >
            <div style={{ flex: 1 }}>
              <div className="fila" style={{ gap: 6 }}>
                <Icono nombre={p.hecho ? 'check' : p.icono} tamano={15} />
                <strong style={{ fontSize: 14 }}>{p.titulo}</strong>
              </div>
              {!p.hecho && (
                <p className="nota--fina" style={{ marginTop: 2 }}>
                  {p.porque}
                </p>
              )}
            </div>
            {p.hecho ? (
              <span className="nota--fina" style={{ flex: 'none', paddingTop: 3 }}>
                hecho
              </span>
            ) : (
              <button
                type="button"
                className="boton boton--chico"
                onClick={() => ir(p.destino)}
                style={{ flex: 'none' }}
              >
                {p.accion}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/*
 * Acá vivía `AtajosSecundarios`, la fila de botones a Progreso, Historial y Foto. Estaba en medio
 * del scroll de Inicio, después de las tarjetas de Plan y Esta semana: para descubrir que esas
 * pantallas existían había que scrollear dos pantallas. Ahora esos destinos son el tab "Más" de
 * `Shell.mobile.tsx`, que es donde se busca una sección — en la barra de navegación.
 */
